import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  CircularProgress,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  AutoAwesome as AutoAwesomeIcon,
  GavelOutlined as GavelIcon,
  BusinessOutlined as BusinessIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import {
  getLegalDocuments,
  saveLegalDocument,
  deleteLegalDocument,
  seedLegalDefaults,
  type LegalDocumentData,
} from '../../../services/ecommerceApi';
import HTMLEditor from './HTMLEditor';
import GuideButton from '../../common/guide/GuideButton';

interface LegalDocumentsSettingsProps {
  companyId: string;
  configId: string;
  companyName?: string;
}

// Belge icinde sirket adinin otomatik yerlestirilecegi yer tutucu
const COMPANY_NAME_TOKEN = '{{SIRKET_ADI}}';
const COMPANY_NAME_TOKEN_REGEX = /\{\{\s*(SIRKET_ADI|FIRMA_ADI|COMPANY_NAME)\s*\}\}/gi;

interface LegalDocRow extends LegalDocumentData {
  _localKey: string;
  _dirty?: boolean;
  _preview?: boolean;
}

// Zorunlu / önerilen belge tipleri (sıralama + etiket)
const DOC_TYPE_META: Array<{ type: string; label: string }> = [
  { type: 'cookie-policy', label: 'Çerez Politikası' },
  { type: 'kvkk', label: 'KVKK Aydınlatma Metni' },
  { type: 'privacy', label: 'Gizlilik Sözleşmesi' },
  { type: 'sales-contract', label: 'Mesafeli Satış Sözleşmesi' },
  { type: 'refund-policy', label: 'İptal ve İade Politikası' },
  { type: 'terms-of-use', label: 'Kullanım Koşulları' },
];

const getTypeLabel = (type?: string): string => {
  const found = DOC_TYPE_META.find((d) => d.type === type);
  return found ? found.label : 'Özel Belge';
};

let localKeyCounter = 0;
const nextLocalKey = () => `legal-${Date.now()}-${localKeyCounter++}`;

const LegalDocumentsSettings: React.FC<LegalDocumentsSettingsProps> = ({ companyId, configId, companyName }) => {
  const [companyNameInput, setCompanyNameInput] = useState<string>(companyName || '');
  const [applyingName, setApplyingName] = useState(false);

  const [rows, setRows] = useState<LegalDocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteTarget, setDeleteTarget] = useState<LegalDocRow | null>(null);

  const notify = (message: string, severity: 'success' | 'error' | 'info' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const loadDocuments = useCallback(async () => {
    if (!configId) return;
    setLoading(true);
    try {
      const res = await getLegalDocuments(configId);
      const list: LegalDocumentData[] = res?.data || [];
      const mapped: LegalDocRow[] = list.map((d) => ({
        ...d,
        showInSwitcher: d.showInSwitcher !== false,
        switcherGroup: d.switcherGroup || '',
        _localKey: nextLocalKey(),
        _dirty: false,
      }));
      mapped.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      setRows(mapped);
    } catch (e) {
      notify('Belgeler yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  }, [configId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    // Sirket adi prop'u sonradan gelirse ve kullanici henuz bir sey yazmadiysa doldur
    if (companyName && companyName.trim() && !companyNameInput.trim()) {
      setCompanyNameInput(companyName.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  // Girilen sirket adini tum belgelerdeki {{SIRKET_ADI}} yer tutucularina uygular ve kaydeder
  const handleApplyCompanyNameToAll = async () => {
    const name = companyNameInput.trim();
    if (!name) {
      notify('Önce şirket adını girin', 'error');
      return;
    }
    if (rows.length === 0) {
      notify('Uygulanacak belge yok', 'info');
      return;
    }
    setApplyingName(true);
    try {
      const updatedRows = rows.map((r) => ({
        ...r,
        content: (r.content || '').replace(COMPANY_NAME_TOKEN_REGEX, name),
        _dirty: true,
      }));
      setRows(updatedRows);

      let savedCount = 0;
      for (const r of updatedRows) {
        if (!r.title || !r.title.trim()) continue;
        await saveLegalDocument(toSavePayload(r));
        savedCount++;
      }
      notify(`Şirket adı ${savedCount} belgeye uygulandı ve kaydedildi`, 'success');
      await loadDocuments();
    } catch (e) {
      notify('Şirket adı uygulanırken hata oluştu', 'error');
    } finally {
      setApplyingName(false);
    }
  };

  const existingTypes = useMemo(
    () => new Set(rows.map((r) => r.documentType).filter(Boolean)),
    [rows]
  );

  const missingRequiredCount = useMemo(
    () => DOC_TYPE_META.filter((m) => !existingTypes.has(m.type)).length,
    [existingTypes]
  );

  const toSavePayload = (r: LegalDocRow): LegalDocumentData => ({
    id: r.id,
    configId,
    documentType: r.documentType || 'custom',
    title: (r.title || '').trim(),
    slug: r.slug,
    content: r.content,
    isActive: r.isActive,
    showInFooter: r.showInFooter,
    showInSwitcher: r.showInSwitcher !== false,
    switcherGroup: (r.switcherGroup || '').trim(),
    displayOrder: r.displayOrder,
  });

  const existingSwitcherGroups = useMemo(() => {
    const names = rows
      .map((r) => (r.switcherGroup || '').trim())
      .filter((name) => name.length > 0);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [rows]);

  const switcherSiblingsFor = (row: LegalDocRow) => {
    if (row.showInSwitcher === false) return [];
    const group = (row.switcherGroup || '').trim().toLocaleLowerCase('tr-TR');
    return rows
      .filter((r) => {
        if (r.showInSwitcher === false) return false;
        return (r.switcherGroup || '').trim().toLocaleLowerCase('tr-TR') === group;
      })
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  };

  const updateRow = (localKey: string, patch: Partial<LegalDocRow>) => {
    setRows((prev) => prev.map((r) => (r._localKey === localKey ? { ...r, ...patch, _dirty: true } : r)));
  };

  const handleSeedDefaults = async () => {
    if (!configId) return;
    setSeeding(true);
    try {
      const res = await seedLegalDefaults(configId);
      notify(res?.message || 'Taslaklar oluşturuldu', 'success');
      await loadDocuments();
    } catch (e) {
      notify('Taslaklar oluşturulurken hata oluştu', 'error');
    } finally {
      setSeeding(false);
    }
  };

  const persistRowOrder = async (ordered: LegalDocRow[]) => {
    const withOrder = ordered.map((r, index) => ({ ...r, displayOrder: index }));
    setRows(withOrder);
    const toSave = withOrder.filter((r) => r.id && r.title?.trim());
    try {
      await Promise.all(toSave.map((r) => saveLegalDocument(toSavePayload(r))));
    } catch (e) {
      notify('Sıra kaydedilirken hata oluştu', 'error');
    }
  };

  const handleMoveRow = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    const current = next[index];
    next[index] = next[target];
    next[target] = current;
    void persistRowOrder(next);
  };

  const handleAddCustom = () => {
    const newRow: LegalDocRow = {
      _localKey: nextLocalKey(),
      configId,
      companyId,
      documentType: 'custom',
      title: 'Yeni Yasal Belge',
      content: '',
      isActive: false,
      showInFooter: true,
      showInSwitcher: true,
      switcherGroup: '',
      displayOrder: rows.length,
      _dirty: true,
      _preview: false,
    };
    setRows((prev) => [...prev, newRow]);
  };

  const handleSave = async (row: LegalDocRow) => {
    if (!row.title || !row.title.trim()) {
      notify('Başlık zorunludur', 'error');
      return;
    }
    setSavingKey(row._localKey);
    try {
      const res = await saveLegalDocument(toSavePayload(row));
      const saved: LegalDocumentData = res?.data || {};
      setRows((prev) =>
        prev.map((r) =>
          r._localKey === row._localKey
            ? { ...r, ...saved, _localKey: r._localKey, _dirty: false }
            : r
        )
      );
      notify('Belge kaydedildi', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Belge kaydedilirken hata oluştu';
      notify(msg, 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleToggleActive = async (row: LegalDocRow, checked: boolean) => {
    // Aç/kapa: kaydı varsa anında persist et, yoksa yalnızca local güncelle
    updateRow(row._localKey, { isActive: checked });
    if (row.id) {
      await handleSave({ ...row, isActive: checked });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const row = deleteTarget;
    setDeleteTarget(null);
    if (!row.id) {
      // Kaydedilmemiş satır: sadece listeden çıkar
      setRows((prev) => prev.filter((r) => r._localKey !== row._localKey));
      return;
    }
    try {
      await deleteLegalDocument(row.id);
      setRows((prev) => prev.filter((r) => r._localKey !== row._localKey));
      notify('Belge silindi', 'success');
    } catch (e) {
      notify('Belge silinirken hata oluştu', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <GavelIcon color="primary" />
          <Typography variant="h6">Yasal Sözleşmeler</Typography>
          <GuideButton guideKey="ecommerce.legalDocuments" size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Çerez Politikası, KVKK Aydınlatma Metni, Gizlilik Sözleşmesi, Mesafeli Satış Sözleşmesi,
          İptal-İade Politikası ve Kullanım Koşulları gibi yasal belgeleri buradan oluşturabilir,
          düzenleyebilir ve yayına alabilirsiniz. Aktif belgeler mağazanızda{' '}
          <code>/legal/&#123;slug&#125;</code> adresinde yayınlanır ve footer'da "Yasal" başlığı altında otomatik listelenir.
          Geçiş anahtarı açık belgeler, sayfalardaki gibi vitrinde birbirine geçer.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            flexWrap: 'wrap',
            p: 2,
            mb: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <TextField
            label="Şirket Adı / Yasal Ünvan"
            value={companyNameInput}
            onChange={(e) => setCompanyNameInput(e.target.value)}
            size="small"
            placeholder="Örn: MT Teknoloji Ltd. Şti."
            InputProps={{ startAdornment: <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ flexGrow: 1, minWidth: 260 }}
            helperText={`Belge metinlerindeki ${COMPANY_NAME_TOKEN} yer tutucularına bu isim yazılır.`}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={handleApplyCompanyNameToAll}
            disabled={applyingName || rows.length === 0}
            startIcon={applyingName ? <CircularProgress size={16} color="inherit" /> : <BusinessIcon />}
            sx={{ mt: 0.5 }}
          >
            Tüm belgelere uygula
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={seeding ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleSeedDefaults}
            disabled={seeding || !configId}
          >
            Zorunlu belgeleri taslak olarak oluştur
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddCustom} disabled={!configId}>
            Yeni belge ekle
          </Button>
        </Box>

        {missingRequiredCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {missingRequiredCount} zorunlu belge tipi henüz eklenmemiş. "Zorunlu belgeleri taslak olarak oluştur"
            ile Türkçe taslaklarını hızlıca ekleyebilirsiniz. (Taslaklar pasif başlar; içeriği gözden geçirip aktifleştirin.)
          </Alert>
        )}
      </Paper>

      {rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Henüz yasal belge yok. Yukarıdan taslak oluşturabilir veya yeni belge ekleyebilirsiniz.</Typography>
        </Paper>
      ) : (
        rows.map((row, index) => (
          <Accordion key={row._localKey} defaultExpanded={!row.id}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                >
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveRow(index, -1);
                    }}
                    aria-label="Yukarı taşı"
                    sx={{ p: 0.25 }}
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={index === rows.length - 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoveRow(index, 1);
                    }}
                    aria-label="Aşağı taşı"
                    sx={{ p: 0.25 }}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>
                  {row.title || getTypeLabel(row.documentType)}
                </Typography>
                <Chip size="small" label={getTypeLabel(row.documentType)} variant="outlined" />
                {row.showInSwitcher !== false && (
                  <Chip
                    size="small"
                    icon={<CompareArrowsIcon />}
                    label={row.switcherGroup?.trim() || 'Geçiş'}
                    variant="outlined"
                    color="primary"
                  />
                )}
                <Chip
                  size="small"
                  color={row.isActive ? 'success' : 'default'}
                  label={row.isActive ? 'Aktif' : 'Pasif'}
                />
                {row._dirty && <Chip size="small" color="warning" label="Kaydedilmedi" />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <TextField
                    label="Başlık"
                    value={row.title || ''}
                    onChange={(e) => updateRow(row._localKey, { title: e.target.value })}
                    size="small"
                    sx={{ flexGrow: 1, minWidth: 240 }}
                  />
                  <TextField
                    label="Slug (opsiyonel)"
                    value={row.slug || ''}
                    onChange={(e) => updateRow(row._localKey, { slug: e.target.value })}
                    size="small"
                    helperText="Boş bırakılırsa başlıktan üretilir"
                    sx={{ minWidth: 220 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!row.isActive}
                        onChange={(_, v) => handleToggleActive(row, v)}
                        color="success"
                      />
                    }
                    label={row.isActive ? 'Aktif (yayında)' : 'Pasif'}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={row.showInFooter !== false}
                        onChange={(_, v) => updateRow(row._localKey, { showInFooter: v })}
                      />
                    }
                    label="Footer'da göster"
                  />
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'divider',
                    bgcolor: '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'flex-start' }}
                      control={
                        <Switch
                          checked={row.showInSwitcher !== false}
                          onChange={(_, v) => updateRow(row._localKey, { showInSwitcher: v })}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CompareArrowsIcon fontSize="inherit" />
                            Sözleşmeler arası geçişte göster
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Açık olan belgeler vitrinde birbirine geçer.
                          </Typography>
                        </Box>
                      }
                    />
                    <TextField
                      size="small"
                      label="Geçiş grubu"
                      value={row.switcherGroup || ''}
                      onChange={(e) => updateRow(row._localKey, { switcherGroup: e.target.value })}
                      disabled={row.showInSwitcher === false}
                      placeholder="Boş = varsayılan grup"
                      helperText="Aynı grubu paylaşan sözleşmeler vitrinde birlikte listelenir."
                      sx={{ minWidth: 220, flexGrow: 1 }}
                    />
                  </Box>
                  {existingSwitcherGroups.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      <Chip
                        size="small"
                        label="Varsayılan"
                        variant={row.switcherGroup ? 'outlined' : 'filled'}
                        color={row.switcherGroup ? 'default' : 'primary'}
                        disabled={row.showInSwitcher === false}
                        onClick={() => updateRow(row._localKey, { switcherGroup: '' })}
                      />
                      {existingSwitcherGroups.map((group) => (
                        <Chip
                          key={group}
                          size="small"
                          label={group}
                          variant={row.switcherGroup === group ? 'filled' : 'outlined'}
                          color={row.switcherGroup === group ? 'primary' : 'default'}
                          disabled={row.showInSwitcher === false}
                          onClick={() => updateRow(row._localKey, { switcherGroup: group })}
                        />
                      ))}
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Vitrin önizlemesi
                    </Typography>
                    {row.showInSwitcher !== false && switcherSiblingsFor(row).length >= 2 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {switcherSiblingsFor(row).map((sibling) => (
                          <Chip
                            key={sibling._localKey}
                            label={sibling.title || getTypeLabel(sibling.documentType)}
                            color={sibling._localKey === row._localKey ? 'primary' : 'default'}
                            variant={sibling._localKey === row._localKey ? 'filled' : 'outlined'}
                            size="small"
                          />
                        ))}
                      </Box>
                    ) : row.showInSwitcher !== false ? (
                      <Typography variant="body2" color="text.secondary">
                        Vitrinde görünmesi için aynı gruba en az bir sözleşme daha ekleyin.
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Bu sözleşme geçiş grubunda değil. İstediğiniz belgelerde anahtarı açıp kaydedin.
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2">İçerik</Typography>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<BusinessIcon fontSize="small" />}
                    onClick={() =>
                      updateRow(row._localKey, {
                        content: `${row.content || ''} ${COMPANY_NAME_TOKEN}`,
                      })
                    }
                  >
                    Şirket adı yer tutucusu ekle
                  </Button>
                </Box>

                <HTMLEditor
                  value={row.content || ''}
                  onChange={(html) => updateRow(row._localKey, { content: html })}
                  companyId={companyId}
                  placeholder="Belge içeriğini buraya yazın... Biçimlendirme için araç çubuğunu kullanın."
                />

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteTarget(row)}
                  >
                    Sil
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={savingKey === row._localKey ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={() => handleSave(row)}
                    disabled={savingKey === row._localKey}
                  >
                    Kaydet
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Belgeyi sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{deleteTarget?.title}" belgesini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Vazgeç</Button>
          <Button color="error" onClick={confirmDelete}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LegalDocumentsSettings;
